import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Box, Typography } from '@mui/material';

import styles from './notesDrawer.module.scss';

export const MIB_CODE_MIN_LENGTH = 6;
export const MIB_CODE_MAX_LENGTH = 25;
export const MIB_CODES_MAX_PER_NOTE = 20;

export interface MibCodebackRowHandle {
  getPendingInput: () => string;
  clearPending: () => void;
}

interface MibCodebackRowProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export const MibCodebackRow = forwardRef<
  MibCodebackRowHandle,
  MibCodebackRowProps
>(function MibCodebackRow({ value, onChange, disabled }, ref) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(ref, () => ({
    getPendingInput: () => input,
    clearPending: () => setInput(''),
  }));

  // Toast pill for input warnings (mirrors the workbench mock's `.toast`).
  const [toast, setToast] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const warn = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2000);
  }, []);

  const tryAddCode = useCallback(
    (raw: string) => {
      const code = raw.trim().toUpperCase();
      if (!code) return;
      if (
        code.length < MIB_CODE_MIN_LENGTH ||
        code.length > MIB_CODE_MAX_LENGTH
      ) {
        warn(
          `Code must be ${MIB_CODE_MIN_LENGTH}–${MIB_CODE_MAX_LENGTH} characters.`,
        );
        return;
      }
      if (value.includes(code)) {
        warn(`Code already added.`);
        return;
      }
      if (value.length >= MIB_CODES_MAX_PER_NOTE) {
        warn(`At most ${MIB_CODES_MAX_PER_NOTE} codes per note.`);
        return;
      }
      onChange([...value, code]);
    },
    [value, onChange, warn],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      if (input.trim()) {
        event.preventDefault();
        tryAddCode(input);
        setInput('');
      } else if (event.key === ',') {
        // Bare commas with no token shouldn't reach the input.
        event.preventDefault();
      }
      return;
    }
    if (event.key === 'Backspace' && input === '' && value.length > 0) {
      event.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (next.endsWith(',')) {
      tryAddCode(next.slice(0, -1));
      setInput('');
      return;
    }
    setInput(next);
  };

  const removeAt = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <Box className={styles.MibRow}>
      <Typography component="span" className={styles.MibRowLabel}>
        MIB Codeback Draft
      </Typography>
      <Box
        className={styles.MibInputWrap}
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        {value.map((code, idx) => (
          <Box key={code} component="span" className={styles.MibTag}>
            {code}
            <button
              type="button"
              className={styles.MibTagRemove}
              aria-label={`Remove ${code}`}
              disabled={disabled}
              onClick={event => {
                event.stopPropagation();
                removeAt(idx);
              }}
            >
              ×
            </button>
          </Box>
        ))}
        <input
          ref={inputRef}
          className={styles.MibInput}
          type="text"
          maxLength={25}
          placeholder={value.length === 0 ? 'Add code…' : ''}
          value={input}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-label="Add MIB code"
        />
      </Box>
      <Box
        className={`${styles.Toast}${toast ? ` ${styles.ToastShow}` : ''}`}
        role="status"
        aria-live="polite"
      >
        {toast}
      </Box>
    </Box>
  );
});
